/*
 * Lemonade Training Oy, copy rights, all right reserved.
 * F032 step 7b: a daily-calendar program. A week strip where empty days stand
 * out, the chosen day's workouts, and the month calendar beside them.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Calendar,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
} from "@lemonadetraining/web-ui";
import { IconCalendar, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import format from "date-fns/format";
import getISOWeek from "date-fns/getISOWeek";
import startOfMonth from "date-fns/startOfMonth";
import { isCalendarDateKey } from "@lemonadetraining/contracts";
import {
  useProgramWorkouts,
  type PlannedWorkout,
  type WorkoutRange,
} from "../../../hooks/useProgramWorkouts";
import { useProgrammeCalendar, type ProgrammeCalendar } from "../../../hooks/useProgrammeCalendar";
import {
  addGymDays,
  gymKeySpan,
  gymWeekStart,
  keyToLocalDate,
  localDateToKey,
} from "../../../lib/gymClock";
import ClockNote from "../../time/ClockNote";
import GymCalendarSkeleton from "../../time/GymCalendarSkeleton";
import { kindLabel } from "../../../lib/workoutKinds";
import { WorkoutSlot } from "./WorkoutSlot";
import { useWorkoutNotes } from "../../../hooks/useWorkoutNotes";
import { usePlannerEditing, type Placement } from "./usePlannerEditing";
import type { PlannerProps } from "./plannerProps";
import { CopyWeekDialog } from "./CopyWeekDialog";

const dayLabel = (key: string) => format(keyToLocalDate(key), "EEEE d MMM");

const firstOfMonthKey = (key: string) => `${key.slice(0, 7)}-01`;

const lastOfMonthKey = (key: string) =>
  localDateToKey(new Date(Number(key.slice(0, 4)), Number(key.slice(5, 7)), 0, 12));

const NO_WORKOUTS: PlannedWorkout[] = [];

export function DailyPlanner(props: PlannerProps) {
  // A programme with no gym stays on this browser's calendar and reads no box.
  const calendar = useProgrammeCalendar(props.program.boxID);
  if (!calendar.ready) {
    return (
      <div className="flex flex-col gap-4" data-testid="daily-planner">
        <GymCalendarSkeleton
          className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7"
          tiles={7}
          tileClassName="h-24 w-full rounded-xl"
          testId="planner-loading"
        />
      </div>
    );
  }
  return <DailyPlannerReady {...props} calendar={calendar} />;
}

function DailyPlannerReady({
  program,
  canManage,
  request,
  onRequestHandled,
  noteLabel,
  calendar,
}: PlannerProps & { calendar: Extract<ProgrammeCalendar, { ready: true }> }) {
  const { gym, zone, todayKey, keyOf, dateOf } = calendar;
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [monthKey, setMonthKey] = useState<string | null>(null);
  const selected = selectedKey ?? todayKey;
  const month = monthKey ?? firstOfMonthKey(selected);
  const weekStart = gymWeekStart(selected);
  const weekEnd = addGymDays(weekStart, 6);
  const week = gymKeySpan(weekStart, weekEnd, zone);

  // The month on the calendar, plus the chosen week when it falls outside it: two small reads, never a growing one.
  const range = useMemo((): WorkoutRange => {
    const gridStart = gymWeekStart(month);
    const gridEnd = addGymDays(gymWeekStart(lastOfMonthKey(month)), 6);
    const window = (from: string, to: string) => {
      const span = gymKeySpan(from, to, zone);
      return { from: new Date(span.startMs), to: new Date(span.endMs - 1) };
    };
    const weekInGrid = weekStart >= gridStart && weekEnd <= gridEnd;
    return {
      kind: "dates",
      windows: weekInGrid
        ? [window(gridStart, gridEnd)]
        : [window(gridStart, gridEnd), window(weekStart, weekEnd)],
    };
  }, [zone, month, weekStart, weekEnd]);

  const data = useProgramWorkouts(program.programmingUID, range);
  const notes = useWorkoutNotes(
    program.programmingUID,
    data.workouts.map((w) => w.id),
    canManage && Boolean(noteLabel)
  );
  const notesFor = noteLabel
    ? {
        label: noteLabel,
        byId: notes.notes,
        failed: notes.status === "error",
        onRetry: notes.reload,
      }
    : undefined;

  const byDay = useMemo(() => {
    const map = new Map<string, PlannedWorkout[]>();
    for (const w of data.workouts) {
      const key = keyOf(w.workoutData.date);
      if (key) map.set(key, [...(map.get(key) ?? []), w]);
    }
    return map;
  }, [data.workouts, keyOf]);
  const onDay = (key: string) => byDay.get(key) ?? NO_WORKOUTS;
  const plannedDates = useMemo(() => [...byDay.keys()].map(keyToLocalDate), [byDay]);
  const days = Array.from({ length: 7 }, (_, i) => addGymDays(weekStart, i));
  const readyDays = days.filter((d) => onDay(d).length > 0).length;

  const [copyOpen, setCopyOpen] = useState(false);

  const choose = (key: string) => {
    setSelectedKey(key);
    if (firstOfMonthKey(key) !== month) setMonthKey(firstOfMonthKey(key));
  };

  const editing = usePlannerEditing({
    programName: program.name,
    workouts: data,
    notes:
      canManage && noteLabel
        ? { label: noteLabel, status: notes.status, byId: notes.notes, save: notes.save }
        : undefined,
    describe: (placement: Placement) => {
      const key = keyOf(placement.date);
      return key ? dayLabel(key) : "No date";
    },
    renderPlacementControl: (placement, onChange) => (
      <label className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">Move to</span>
        <Input
          type="date"
          className="w-auto"
          value={keyOf(placement.date) ?? ""}
          onChange={(e) => {
            if (isCalendarDateKey(e.target.value)) {
              onChange({ ...placement, date: dateOf(e.target.value) });
            }
          }}
          aria-label="Move to date"
          data-testid="move-to-date"
        />
      </label>
    ),
  });

  useEffect(() => {
    if (!request) return;
    if (request === "add") editing.openCreate({ date: dateOf(selected) });
    if (request === "copy") setCopyOpen(true);
    onRequestHandled();
  }, [request]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthCalendar = (
    <Calendar
      mode="single"
      selected={keyToLocalDate(selected)}
      onSelect={(date) => date && choose(localDateToKey(date))}
      month={keyToLocalDate(month)}
      onMonthChange={(date) => setMonthKey(localDateToKey(startOfMonth(date)))}
      today={keyToLocalDate(todayKey)}
      modifiers={{ planned: plannedDates }}
      modifiersClassNames={{
        planned:
          "after:pointer-events-none after:absolute after:bottom-1 after:left-1/2 after:size-1 after:-translate-x-1/2 after:rounded-full after:bg-current",
      }}
      classNames={{ today: "rounded-md border border-primary font-semibold" }}
      className="rounded-xl border"
    />
  );

  return (
    <div className="flex flex-col gap-4" data-testid="daily-planner">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Previous week"
          onClick={() => choose(addGymDays(selected, -7))}
        >
          <IconChevronLeft className="size-4" aria-hidden="true" />
        </Button>
        <h2 className="text-base font-semibold tabular-nums" aria-live="polite">
          Week {getISOWeek(keyToLocalDate(weekStart))} ·{" "}
          {format(keyToLocalDate(weekStart), "d MMM")} to {format(keyToLocalDate(weekEnd), "d MMM")}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Next week"
          onClick={() => choose(addGymDays(selected, 7))}
        >
          <IconChevronRight className="size-4" aria-hidden="true" />
        </Button>
        {selected !== todayKey && (
          <Button variant="outline" onClick={() => choose(todayKey)}>
            Today
          </Button>
        )}
        {data.status === "ready" && (
          <span className="text-sm text-muted-foreground" data-testid="days-ready">
            {readyDays} of 7 days planned
          </span>
        )}
        <span className="flex-1" />
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" className="lg:hidden">
              <IconCalendar className="size-4" aria-hidden="true" />
              Calendar
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            {monthCalendar}
          </PopoverContent>
        </Popover>
      </div>
      <ClockNote
        variant="static"
        mode="gym"
        city={gym.city}
        differs={gym.differs([week.startMs, week.endMs - 1])}
        className="-mt-2"
      />

      <ol className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7" aria-label="This week">
        {days.map((key) => {
          const planned = onDay(key);
          const isSelected = key === selected;
          const isToday = key === todayKey;
          const kinds = Array.from(
            new Set(planned.flatMap((w) => w.workoutData.tags ?? []).map((t) => kindLabel(t)))
          );
          return (
            <li key={key} className="min-w-0">
              <button
                type="button"
                onClick={() => choose(key)}
                aria-pressed={isSelected}
                aria-label={`${dayLabel(key)}${isToday ? ", today" : ""}: ${
                  planned.length === 0
                    ? "nothing planned"
                    : planned.map((w) => w.workoutData.title || "Untitled workout").join(", ")
                }`}
                className={cn(
                  "flex h-full min-h-24 w-full min-w-0 flex-col items-start gap-1 rounded-xl border p-2 text-left text-sm transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  planned.length === 0 && "border-dashed",
                  isSelected && "border-2 border-primary"
                )}
                data-testid={`day-${key}`}
              >
                <span className="text-xs font-medium text-muted-foreground">
                  {format(keyToLocalDate(key), "EEE d")}
                  {isToday && " · today"}
                </span>
                {planned.length === 0 ? (
                  <span className="text-muted-foreground">
                    {canManage ? "+ Plan this day" : "Nothing planned"}
                  </span>
                ) : (
                  <>
                    <span className="line-clamp-2 font-medium">
                      {planned.map((w) => w.workoutData.title || "Untitled workout").join(" + ")}
                    </span>
                    <span className="flex flex-wrap gap-1">
                      {kinds.map((k) => (
                        <span
                          key={k}
                          className="rounded-full border px-1.5 text-xs text-muted-foreground"
                        >
                          {k}
                        </span>
                      ))}
                    </span>
                  </>
                )}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
        <WorkoutSlot
          heading={dayLabel(selected)}
          workouts={onDay(selected)}
          status={data.status}
          canManage={canManage}
          emptyText={
            canManage ? "Nothing planned for this day yet." : "Nothing planned for this day."
          }
          onAdd={() => editing.openCreate({ date: dateOf(selected) })}
          onEdit={editing.openEdit}
          onDuplicate={editing.duplicate}
          onRemove={editing.requestRemove}
          onRetry={data.reload}
          notes={canManage ? notesFor : undefined}
        />
        <aside className="hidden flex-col gap-2 lg:flex" aria-label="Month">
          {monthCalendar}
          <p className="px-1 text-xs text-muted-foreground">
            A dot under a date means workouts are planned.
          </p>
        </aside>
      </div>
      {editing.nodes}
      <CopyWeekDialog
        open={copyOpen}
        onOpenChange={setCopyOpen}
        program={program}
        source={{ kind: "dates", fromKey: weekStart, gym, workouts: days.flatMap(onDay) }}
        status={data.status}
        create={data.create}
        onCopied={data.reload}
      />
    </div>
  );
}
